import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button } from 'react-bootstrap';
import { Bank } from 'react-bootstrap-icons';
import { Organization } from '../../../models';
import {
  calendarDaysUntilPeriodEnd,
  parseBillingPeriodEnd,
  shouldPromptVesEarlyRenew,
} from '../../../utils/billingPeriodEnd';

interface VesPaymentReminderBannerProps {
  organization: Organization | null;
}

/**
 * Soft-prompt windows for VES:
 * - Early renew: still in-period, period ends within 7 days
 * - Grace: period already ended, within 10-day grace
 */
function shouldShowGraceReminder(organization: Organization | null): boolean {
  if (!organization?.billing) return false;
  if (organization.billing.paymentMethod !== 'ves') return false;
  if (organization.billing.subscriptionStatus !== 'active') return false;

  const endRaw = organization.billing.currentPeriodEnd;
  const periodEnd = parseBillingPeriodEnd(endRaw);
  if (!periodEnd) return false;

  const now = new Date();
  if (now <= periodEnd) return false;

  const daysSincePeriodEnd = Math.floor(
    (now.getTime() - periodEnd.getTime()) / (1000 * 60 * 60 * 24)
  );
  return daysSincePeriodEnd <= 10;
}

const VesPaymentReminderBanner: React.FC<VesPaymentReminderBannerProps> = ({
  organization,
}) => {
  const navigate = useNavigate();

  const earlyRenew = shouldPromptVesEarlyRenew(organization?.billing);
  const grace = shouldShowGraceReminder(organization);

  if (!earlyRenew && !grace) return null;

  const endRaw = organization?.billing?.currentPeriodEnd;
  const periodEnd = parseBillingPeriodEnd(endRaw);
  const formattedDate = periodEnd
    ? periodEnd.toLocaleDateString('es-VE', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;
  const daysLeft =
    earlyRenew && periodEnd ? calendarDaysUntilPeriodEnd(periodEnd) : null;

  const goRenew = () =>
    navigate(earlyRenew ? '/paywall?renew=1' : '/paywall', {
      state: earlyRenew ? { vesRenew: true } : undefined,
    });

  if (earlyRenew) {
    return (
      <Alert
        variant="light"
        className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 py-2 px-3 border"
      >
        <span>
          <strong>Renueva con anticipación</strong>
          {daysLeft === 0
            ? ' · Tu plan vence hoy'
            : daysLeft === 1
              ? ' · 1 día restante'
              : daysLeft != null
                ? ` · ${daysLeft} días restantes`
                : null}
          {formattedDate && <> · vence el {formattedDate}</>}
          {' · '}
          Envía tu transferencia en Bolívares y suma un mes a tu período actual
        </span>
        <Button variant="outline-secondary" size="sm" onClick={goRenew}>
          <Bank size={16} className="me-1" />
          Renovar
        </Button>
      </Alert>
    );
  }

  return (
    <Alert
      variant="warning"
      className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 py-2 px-3"
    >
      <span>
        <strong>Pago mensual pendiente</strong>
        {formattedDate && <> · Tu plan venció el {formattedDate}</>}
        {' · '}
        Por favor, envía tu transferencia en Bolívares
        <span className="text-muted small ms-1">(10 días de gracia)</span>
      </span>
      <Button variant="outline-warning" size="sm" onClick={goRenew}>
        <Bank size={16} className="me-1" />
        Pagar
      </Button>
    </Alert>
  );
};

export default VesPaymentReminderBanner;
